import { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useBuildingKey, getAllUniqueRiskTypesFromLevel, getAllUniqueHazardsFromLevel, RiskConstructionData, HazardData } from '../../Helpers/utils';
import { BaseComponentImpl, RoomImpl, LevelImpl } from '../../types/implementations';
import { useBuilding } from '../../Helpers/BuildingContext';
import { Building, PhotoModel } from '../../types/protocols';
import { BaseType } from '../../types/Enums';
import { getAllComponentsForRooms, getComponentRoomKey } from '../../Helpers/ComponentRoomUtils';
import { roomIsOnLevel } from '../../Helpers/roomUtils';
import { allRoomsVerified, FloorAreaVerificationState, getFloorAreaVerificationState } from '../../Helpers/floorAreaVerification';

interface UseLevelReturn {
    buildingKey: string | null;
    levelKey: string | undefined;
    level: LevelImpl | undefined;
    currentBuilding: Building | null;
    loadingBuilding: boolean;
    buildingError: string | null;
    fetchBuilding: (key: string) => Promise<void>;
    currentBlueprint: PhotoModel | undefined;
    isNoLevelCase: boolean;
    /** Rooms whose primary level this is: the list, the counts, the components and the area sums. */
    rooms: RoomImpl[];
    /** Rooms drawn on this level's drawing: `rooms` plus rooms spanning several levels that include this one (multi-level rooms D9). */
    drawnRooms: RoomImpl[];
    allUniqueLevelComponents: BaseComponentImpl[];
    componentsForBlueprint: BaseComponentImpl[];
    totalLevelCeilingArea: number;
    totalLevelWallArea: number;
    totalLevelFloorArea: number;
    totalLevelCeilingAreaExcludingOpenings: number;
    totalLevelWallAreaExcludingOpenings: number;
    totalLevelFloorAreaExcludingOpenings: number;
    utilitiesCount: number;
    fixedFurnishingsCount: number;
    riskConstructions: RiskConstructionData[];
    hazards: HazardData[];
    allLevels: LevelImpl[];
    availableBlueprints: number;
    roomFloorAreas: Map<string, number>;
    /** Golvyta uppmätt på ritning per room (web#1383), derived from the room and its floors. */
    roomFloorAreaStates: Map<string, FloorAreaVerificationState>;
    /** The level's Golvyta is verified only when every room on it is (rooms without floors block it). */
    allRoomsFloorAreaVerified: boolean;
}

export const useLevel = (): UseLevelReturn => {
    const buildingKey = useBuildingKey();
    const { levelId: levelKey } = useParams<{ levelId: string }>();
    const [level, setLevel] = useState<LevelImpl | undefined>(undefined);
    const [currentBlueprint, setCurrentBlueprint] = useState<PhotoModel | undefined>(undefined);

    const { currentBuilding, buildingError, loadingBuilding, fetchBuilding } = useBuilding();

    useEffect(() => {
        if (buildingKey) {
            fetchBuilding(buildingKey);
        }
    }, [buildingKey, fetchBuilding]);

    useEffect(() => {
        if (currentBuilding && levelKey) {
            if (levelKey === "no-level") {
                setLevel(undefined);
                setCurrentBlueprint(undefined);
                return;
            }

            const foundLevel = currentBuilding.model.structure?.levels?.[levelKey];
            setLevel(foundLevel ? new LevelImpl(foundLevel) : undefined);

            if (foundLevel) {
                if (foundLevel.currentBlueprintKey && foundLevel.blueprints?.[foundLevel.currentBlueprintKey]) {
                    setCurrentBlueprint(foundLevel.blueprints[foundLevel.currentBlueprintKey]);
                } else {
                    setCurrentBlueprint(undefined);
                }
            }
        }
    }, [currentBuilding, levelKey]);

    const isNoLevelCase = levelKey === "no-level";

    // Filter rooms for this level
    const rooms = useMemo(() => {
        if (!currentBuilding?.model.structure?.rooms) return [];
        return Object.values(currentBuilding.model.structure.rooms)
            .filter((room) => {
                if (isNoLevelCase) {
                    const roomLevelKey = room.levelKey;
                    const levelKeys = currentBuilding.model.structure?.levels
                        ? Object.keys(currentBuilding.model.structure.levels)
                        : [];
                    return !roomLevelKey || roomLevelKey.trim() === '' || !levelKeys.includes(roomLevelKey);
                }
                return room.levelKey === level?.key;
            })
            .map((item) => new RoomImpl(item));
    }, [currentBuilding, isNoLevelCase, level?.key]);

    // Markers on the drawing also include rooms that span several levels and
    // reach this one; they stay out of `rooms` so nothing is counted twice.
    const drawnRooms = useMemo(() => {
        const levelKey = level?.key;
        if (isNoLevelCase || !levelKey || !currentBuilding?.model.structure?.rooms) return rooms;
        const primaryKeys = new Set(rooms.map(room => room.key));
        const spanning = Object.values(currentBuilding.model.structure.rooms)
            .filter(room => room && !primaryKeys.has(room.key) && roomIsOnLevel(room, levelKey))
            .map(item => new RoomImpl(item));
        return spanning.length > 0 ? [...rooms, ...spanning] : rooms;
    }, [currentBuilding, isNoLevelCase, level?.key, rooms]);

    const roomKeys = useMemo(() => rooms.map(room => room.key), [rooms]);

    // All components belonging to this level (through rooms)
    const allUniqueLevelComponents = useMemo(() => {
        if (!currentBuilding || roomKeys.length === 0) return [];
        return getAllComponentsForRooms(currentBuilding, roomKeys);
    }, [currentBuilding, roomKeys]);

    // Components with blueprint locations for the current blueprint
    const componentsForBlueprint = useMemo((): BaseComponentImpl[] => {
        if (isNoLevelCase || !level?.currentBlueprintKey) return [];
        const blueprintKey = level.currentBlueprintKey;
        return allUniqueLevelComponents.filter(component =>
            component.blueprintLocations && component.blueprintLocations[blueprintKey]
        );
    }, [isNoLevelCase, level?.currentBlueprintKey, allUniqueLevelComponents]);

    // Consolidated area calculations - single pass through components
    const levelAreas = useMemo(() => {
        const defaultAreas = {
            ceiling: 0, wall: 0, floor: 0,
            ceilingExcluding: 0, wallExcluding: 0, floorExcluding: 0
        };

        if (!currentBuilding) return defaultAreas;

        const components = Object.values(currentBuilding.model.structure?.components || {});

        return components.reduce((acc, c) => {
            const roomKey = getComponentRoomKey(c);
            if (!roomKey || !roomKeys.includes(roomKey)) return acc;

            const comp = new BaseComponentImpl(c);
            const total = comp.totalArea(currentBuilding) || 0;
            const excluding = comp.totalAreaExcludingWindowsAndDoors(currentBuilding) || 0;

            switch (c.type) {
                case BaseType.Ceiling:
                    acc.ceiling += total;
                    acc.ceilingExcluding += excluding;
                    break;
                case BaseType.Wall:
                    acc.wall += total;
                    acc.wallExcluding += excluding;
                    break;
                case BaseType.Floor:
                    acc.floor += total;
                    acc.floorExcluding += excluding;
                    break;
            }
            return acc;
        }, { ...defaultAreas });
    }, [currentBuilding, roomKeys]);

    // Destructure for backwards compatibility with existing code
    const totalLevelCeilingArea = levelAreas.ceiling;
    const totalLevelWallArea = levelAreas.wall;
    const totalLevelFloorArea = levelAreas.floor;
    const totalLevelCeilingAreaExcludingOpenings = levelAreas.ceilingExcluding;
    const totalLevelWallAreaExcludingOpenings = levelAreas.wallExcluding;
    const totalLevelFloorAreaExcludingOpenings = levelAreas.floorExcluding;

    // Per-room floor areas and verification states (precomputed to avoid recalculating in render loop)
    const roomFloorData = useMemo(() => {
        const areas = new Map<string, number>();
        const states = new Map<string, FloorAreaVerificationState>();
        if (!currentBuilding) return { areas, states };
        const components = Object.values(currentBuilding.model.structure?.components || {});
        for (const room of rooms) {
            const floors = components.filter(c => c.type === BaseType.Floor && getComponentRoomKey(c) === room.key);
            const area = floors.reduce((total, c) => {
                const comp = new BaseComponentImpl(c);
                return total + (comp.totalArea(currentBuilding) || 0);
            }, 0);
            areas.set(room.key, area);
            states.set(room.key, getFloorAreaVerificationState(room.floorAreaVerification, floors));
        }
        return { areas, states };
    }, [currentBuilding, rooms]);
    const roomFloorAreas = roomFloorData.areas;
    const roomFloorAreaStates = roomFloorData.states;
    const allRoomsFloorAreaVerified = useMemo(
        () => allRoomsVerified(Array.from(roomFloorAreaStates.values())),
        [roomFloorAreaStates]
    );

    // Utilities count derived from allUniqueLevelComponents
    const utilitiesCount = useMemo(() =>
        allUniqueLevelComponents.filter(c => c.type === BaseType.Utility).length,
        [allUniqueLevelComponents]
    );
    const fixedFurnishingsCount = useMemo(() =>
        allUniqueLevelComponents.filter(c => c.type === BaseType.FixedFurnishing).length,
        [allUniqueLevelComponents]
    );

    // Risk constructions and hazards
    const riskConstructions = useMemo((): RiskConstructionData[] => {
        if (isNoLevelCase || !currentBuilding || !levelKey) return [];
        return getAllUniqueRiskTypesFromLevel(currentBuilding, levelKey);
    }, [currentBuilding, levelKey, isNoLevelCase]);

    const hazards = useMemo((): HazardData[] => {
        if (isNoLevelCase || !currentBuilding || !levelKey) return [];
        return getAllUniqueHazardsFromLevel(currentBuilding, levelKey);
    }, [currentBuilding, levelKey, isNoLevelCase]);

    // All levels for navigation buttons
    const allLevels = useMemo(() => {
        if (!currentBuilding?.model.structure?.levels || isNoLevelCase) return [];
        return Object.values(currentBuilding.model.structure.levels)
            .map(levelData => new LevelImpl(levelData))
            .sort((a, b) => a.number - b.number);
    }, [currentBuilding, isNoLevelCase]);

    const availableBlueprints = useMemo(() => {
        if (isNoLevelCase || !level?.blueprints) return 0;
        return Object.keys(level.blueprints).length;
    }, [isNoLevelCase, level?.blueprints]);

    return {
        buildingKey,
        levelKey,
        level,
        currentBuilding,
        loadingBuilding,
        buildingError,
        fetchBuilding,
        currentBlueprint,
        isNoLevelCase,
        rooms,
        drawnRooms,
        allUniqueLevelComponents,
        componentsForBlueprint,
        totalLevelCeilingArea,
        totalLevelWallArea,
        totalLevelFloorArea,
        totalLevelCeilingAreaExcludingOpenings,
        totalLevelWallAreaExcludingOpenings,
        totalLevelFloorAreaExcludingOpenings,
        utilitiesCount,
        fixedFurnishingsCount,
        riskConstructions,
        hazards,
        allLevels,
        availableBlueprints,
        roomFloorAreas,
        roomFloorAreaStates,
        allRoomsFloorAreaVerified,
    };
};
